import React, { useCallback } from "react";
import { graphql, useFragment } from "react-relay";
import { TodoCircleStatusWithResponse } from "../features/TodoCategory/TodoCircleStatusWithResponse";

import { TodoListItemHeader } from "widgets/src/TodoListItem/TodoListItemHeader";
import { useOpenCamera } from "./mobile/useImageActionSheet";
import { useTodoEditResponseImage } from "../features/TodoEdit/TodoEditNewMain/TodoEditNewMainFooter/useTodoEditResponseImage";
import {
  TodoListItemContainer,
  TodoListItemContainerContent,
} from "widgets/src/TodoListItem/TodoListItemContainer";
import { TodoListItemMainContentDetails } from "widgets/src/TodoListItem/TodoListItemMainContentDetails";
import { TodoListItemPriority } from "widgets/src/TodoListItem/TodoListItemPriority";
import { TodoListItemMainContentText } from "widgets/src/TodoListItem/TodoListItemMainContentText";
import { TodoListItemFooter } from "widgets/src/TodoListItem/TodoListItemFooter/TodoListItemFooter";
import { useSizeClass } from "../common/Utils/useSizeClass";
import { TodoListItem_todo$key } from "./__generated__/TodoListItem_todo.graphql";
import { usePersistedEmployeeId } from "../OS/Login/usePersistedEmployeeId";

export const TodoListItem = React.memo(TodoListItemC);
function TodoListItemC(props: {
  disabled?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  isNew?: boolean;
  todo: TodoListItem_todo$key;
  customHeader?: React.ReactNode;
  openTodo(fromResponse: boolean, todoId: string): void;
}) {
  const sizeClass = useSizeClass().width;
  const todo = useFragment(
    graphql`
      fragment TodoListItem_todo on Todo {
        id
        status
        ordinal
        response {
          __typename
        }
        canWrite

        isOrg
        body
        formattedBody
        canChangeStatus

        ...TodoListItemMainContentText_todo
        ...TodoListItemPriority_todo
        ...TodoListItemFooter_todo
        ...TodoCircleStatusWithResponse_todo
        ...TodoListItemMainContentDetails_todo
        ...TodoListItemHeader_todo
      }
    `,
    props.todo
  );

  const { addImage } = useTodoEditResponseImage({ todoId: todo.id });

  const { openCamera } = useOpenCamera({
    onAddImage(uri, { width, height }) {
      addImage(uri, { width, height });
      props.openTodo(true, todo.id);
    },
  });

  const onStatusUpdateNeedsAction = useCallback(
    (focusResponse: boolean) => {
      if (focusResponse) {
        if (todo.response?.__typename === "TodoResponseImage") {
          openCamera();
        } else {
          props.openTodo(true, todo.id);
        }
      } else {
        props.openTodo(false, todo.id);
      }
    },
    [openCamera, props, todo.id, todo.response?.__typename]
  );
  const { currentEmployeeId } = usePersistedEmployeeId();

  return (
    <TodoListItemContainer
      onPress={() => props.openTodo(false, todo.id)}
      disabled={props.disabled}
      isNew={props.isNew}
      isFirst={props.isFirst}
      isLast={props.isLast}
    >
      {props.customHeader ? (
        props.customHeader
      ) : (
        <TodoListItemHeader todo={todo} />
      )}
      <TodoListItemContainerContent>
        <TodoListItemPriority todo={todo} />
        <TodoCircleStatusWithResponse
          todo={todo}
          disabled={props.disabled || !todo.canWrite}
          onStatusUpdateNeedsAction={onStatusUpdateNeedsAction}
        />
        <TodoListItemMainContentText todo={todo} />
        <TodoListItemMainContentDetails todo={todo} />
      </TodoListItemContainerContent>
      <TodoListItemFooter
        todo={todo}
        sizeClass={sizeClass}
        currentEmployeeId={currentEmployeeId ?? undefined}
      />
    </TodoListItemContainer>
  );
}
